import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, ArrowLeft, Calendar, PhoneCall } from "lucide-react";
import { MentorTierBadge } from "@/components/MentorTierBadge";
import { useMentors } from "@/hooks/useMentors";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { supabase } from "@/integrations/supabase/client";
import { safeSetItem, safeGetItem, safeRemoveItem } from "@/lib/safe-storage";
import ComplianceFooter from "@/components/ComplianceFooter";
import { DiscoveryCallBooking } from "@/components/DiscoveryCallBooking";
import { MentorProgramsDisplay } from "@/components/MentorProgramsDisplay";
import { MentorExampleWorksDisplay } from "@/components/MentorExampleWorksDisplay";

interface MentorProfileProps {
  overrideMentorId?: string;
}

const MentorProfile = ({ overrideMentorId }: MentorProfileProps = {}) => {
  const { mentorId: paramMentorId } = useParams<{ mentorId: string }>();
  const mentorId = overrideMentorId || paramMentorId;
  const { mentors, loading, error } = useMentors();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [autoOpenDiscoveryCall, setAutoOpenDiscoveryCall] = useState(false);
  const discoverySectionRef = useRef<HTMLDivElement | null>(null);
  const scrollAttemptsRef = useRef(0);

  const mentor = mentors.find(m => m.id === mentorId);
  const otherMentors = mentors.filter(m => m.id !== mentor?.id);

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session?.user);
      
      // Check if user just authenticated for a discovery call
      const pendingDiscoveryCall = safeGetItem('pendingDiscoveryCall');
      if (pendingDiscoveryCall === mentorId && session?.user) {
        setAutoOpenDiscoveryCall(true);
        safeRemoveItem('pendingDiscoveryCall');
      }
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session?.user);
    });

    return () => subscription.unsubscribe();
  }, [mentorId]);

  // Auto-scroll to discovery call section after auth redirect
  useEffect(() => {
    if (!autoOpenDiscoveryCall) return;

    const tryScroll = () => {
      if (discoverySectionRef.current) {
        discoverySectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };

    // Try immediately and then retry a few times while content mounts
    if (tryScroll()) return;

    scrollAttemptsRef.current = 0;
    const interval = setInterval(() => {
      scrollAttemptsRef.current += 1;
      if (tryScroll() || scrollAttemptsRef.current > 15) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [autoOpenDiscoveryCall, mentor]);

  const handleBookMentor = async () => {
    if (isAuthenticated === null) {
      // Still checking authentication, wait
      return;
    }

    if (isAuthenticated) {
      // User is authenticated, navigate to student dashboard with booking info
      // Store the full mentor data for immediate use
      safeSetItem('startBookingForMentor', JSON.stringify({
        id: mentor.id,
        first_name: mentor.first_name,
        last_name: mentor.last_name,
        specialty: mentor.specialty,
        hourly_rate: mentor.hourly_rate,
        profile_image_url: mentor.profile_image_url,
        tier: mentor.tier
      }));
      navigate('/dashboard/student');
    } else {
      // User is not authenticated, store booking intent and redirect to auth
      safeSetItem('startBookingForMentor', JSON.stringify({
        id: mentor.id,
        first_name: mentor.first_name,
        last_name: mentor.last_name,
        specialty: mentor.specialty,
        hourly_rate: mentor.hourly_rate,
        profile_image_url: mentor.profile_image_url,
        tier: mentor.tier
      }));
      safeSetItem('redirectAfterLogin', '/dashboard/student');
      navigate('/auth/student');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
        <Card className="obsidian-card p-8">
          <h2 className="text-2xl font-bold text-glow mb-4">Error Loading Mentor</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button asChild className="energy-button">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>
        </Card>
      </div>
    );
  }

  if (!mentor) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center">
        <Card className="obsidian-card p-8">
          <h2 className="text-2xl font-bold text-glow mb-4">Mentor Not Found</h2>
          <Button asChild className="energy-button">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>
        </Card>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Header */}
      <header className="border-b border-border obsidian-glass">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="outline" size="sm" asChild>
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
            <div className="flex items-center gap-3">
              <img 
                src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
                alt="Ascendry" 
                className="h-8 w-auto"
              />
              <span className="hidden md:block text-2xl font-bold text-glow">
                Ascendry
              </span>
            </div>
            <h1 className="text-xl font-bold text-glow">Mentor Profile</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="obsidian-card energy-glow">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-start space-y-6 md:space-y-0 md:space-x-8">
                {/* Profile Image */}
                <div className="flex justify-center md:justify-start">
                  <div className="relative">
                    <img
                      src={mentor.profile_image_url || '/lovable-uploads/1c000ebd-7faf-4987-a62e-8c04a9a373a2.png'}
                      alt={`${mentor.first_name} ${mentor.last_name}`}
                      className="w-48 h-48 rounded-lg object-cover border-2 border-primary"
                    />
                    <MentorTierBadge tier={mentor.tier} mentorName={`${mentor.first_name} ${mentor.last_name}`} className="absolute -bottom-2 -right-2" />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-3xl font-bold text-energy mb-2">
                    {mentor.first_name} {mentor.last_name}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-4">{mentor.specialty}</p>
                  
                  <div className="flex items-center justify-center md:justify-start space-x-4 mb-6">
                    <div className="flex items-center space-x-1">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-medium">{mentor.rating.toFixed(1)}</span>
                      <span className="text-sm text-muted-foreground">({mentor.total_sessions} sessions)</span>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="mb-6">
                    <div className="text-2xl font-bold text-destructive">
                      ${(mentor.hourly_rate / 100).toFixed(0)}/hr
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {(mentor.hourly_rate / 100).toFixed(1)} Tokens/hr
                    </div>
                  </div>

                  {/* Book Button */}
                  <Button 
                    onClick={handleBookMentor}
                    className="energy-button energy-glow w-full md:w-auto"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Session
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-8">
              {/* Bio */}
              <div>
                <h3 className="text-xl font-bold text-glow mb-4">About</h3>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{mentor.bio}</p>
              </div>

              {/* Expertise */}
              <div>
                <h3 className="text-xl font-bold text-glow mb-4">Expertise</h3>
                <div className="flex flex-wrap gap-2">
                  {mentor.expertise.map((skill, index) => (
                    <Badge key={index} variant="secondary" className="text-sm">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Credits */}
              <div>
                <h3 className="text-xl font-bold text-glow mb-4">Credits & Achievements</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {mentor.credits.join(', ')}
                </p>
              </div>

              {/* Example Works */}
              <MentorExampleWorksDisplay mentorId={mentor.id} />
            </CardContent>
          </Card>

          {/* Session Programs */}
          <div className="mt-8">
            <MentorProgramsDisplay 
              mentorId={mentor.id}
              mentorName={`${mentor.first_name} ${mentor.last_name}`}
            />
          </div>

          {/* Discovery Call Section */}
          {mentor.discovery_call_enabled && (
            <div ref={discoverySectionRef} className="mt-8">
              <DiscoveryCallBooking
                mentorId={mentor.id}
                mentorName={`${mentor.first_name} ${mentor.last_name}`}
                mentorSpecialty={mentor.specialty}
                autoOpen={autoOpenDiscoveryCall}
              />
            </div>
          )}

          {/* Other Mentors Section */}
          {otherMentors.length > 0 && (
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-glow mb-6 text-center">Other Mentors</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {otherMentors.map((otherMentor) => (
                  <Card key={otherMentor.id} className="obsidian-card hover:energy-glow transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <img
                            src={otherMentor.profile_image_url || '/lovable-uploads/1c000ebd-7faf-4987-a62e-8c04a9a373a2.png'}
                            alt={`${otherMentor.first_name} ${otherMentor.last_name}`}
                            className="w-16 h-16 rounded-lg object-cover border-2 border-primary"
                          />
                          <MentorTierBadge tier={otherMentor.tier} mentorName={`${otherMentor.first_name} ${otherMentor.last_name}`} className="absolute -bottom-1 -right-1 scale-75" />
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="font-bold text-lg text-energy mb-1">
                            {otherMentor.first_name} {otherMentor.last_name}
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">{otherMentor.specialty}</p>
                          
                          <div className="flex items-center space-x-1 mb-2">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-medium">{otherMentor.rating?.toFixed(1) || '0.0'}</span>
                            <span className="text-xs text-muted-foreground">({otherMentor.total_sessions || 0} sessions)</span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-bold text-destructive">
                                ${(otherMentor.hourly_rate / 100).toFixed(0)}/hr
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {(otherMentor.hourly_rate / 100).toFixed(1)} Tokens/hr
                              </div>
                            </div>
                            
                            <Button 
                              variant="outline" 
                              size="sm"
                              asChild
                            >
                              <Link to={`/mentor/${otherMentor.id}`}>
                                View Profile
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default MentorProfile;